import { GET_SEARCH_ANALYTICS_AGGREGATED_DATA_NAME } from "@/lib/ai/tools/get-search-analytics-aggregated-data/constants";
import type { GetSearchAnalyticsAggregatedDataOutput } from "@/lib/ai/tools/get-search-analytics-aggregated-data/server";
import { expectVisualizationObjectOutput } from "@/lib/ai/visualization/data/adapters/adapter-contract";
import {
  alignCompareTimeseries,
  buildDataset,
  buildFields,
  extractArray,
  isRecord,
  resolveCompareAlignmentMode,
  resolveCompareDateRanges,
  toDatasetRows,
  toString,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import {
  SA_COMPARE_DERIVED_RANK_DEPENDENCIES,
  SA_COMPARE_TIMESERIES_METRICS,
  SA_GROUP_METRICS,
  SA_TIMESERIES_DERIVED_RANK_DEPENDENCIES,
  SA_TIMESERIES_METRICS,
} from "@/lib/ai/visualization/data/adapters/get-search-analytics-aggregated-data/aggregated-metrics";
import { buildSaGroupedDatasets } from "@/lib/ai/visualization/data/adapters/get-search-analytics-aggregated-data/grouped-datasets";
import {
  SA_COMPARE_PROFILE,
  SA_GROUPS_PROFILE,
  SA_TIMESERIES_PROFILE,
} from "@/lib/ai/visualization/data/adapters/get-search-analytics-aggregated-data/profiles";
import { attachDerivedRankDependencies } from "@/lib/ai/visualization/data/adapters/get-search-analytics-aggregated-data/rank-dependencies";
import {
  buildCompareMetricFieldEntries,
  buildCompareTimeseriesRowsFromMetrics,
  buildMetricFieldEntries,
  buildTimeseriesRowsFromMetrics,
  constrainMetricFieldsToFamilies,
  hasCoRealizedMetricFamilyPair,
  readFirstPathNumber,
  resolveCompareDefaultMetricKeys,
  resolveMetricChartProfile,
  resolveRequestedMetricFamiliesWithLegacyFallback,
} from "@/lib/ai/visualization/data/adapters/metric-binding";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";

const SA_REQUESTED_METRIC_FAMILIES = {
  CLICKS: "sumOfClicks",
  IMPRESSIONS: "sumOfImpressions",
  CTR: "ctr",
  AVERAGE_OF_RANK: "avgOfRank",
  DISTINCT_QUERIES: "distinctQueries",
  DISTINCT_URLS: "distinctURLs",
} as const;

const SA_LEGACY_DEPENDENCY_RULES = [
  {
    derivedMetric: "CTR",
    dependencyMetrics: ["CLICKS", "IMPRESSIONS"],
  },
  {
    derivedMetric: "AVERAGE_OF_RANK",
    dependencyMetrics: ["IMPRESSIONS"],
  },
] as const;

function hasSaGroupedItems(items: unknown[]): boolean {
  return items.some(
    (item) =>
      isRecord(item) && (isRecord(item.queryGroup) || isRecord(item.urlGroup)),
  );
}

/**
 * SA aggregated 出力を timeseries / categorical dataset 群へ変換する。
 *
 * @remarks
 * flat trend・grouped row・group summary・period compare を 1 つの入口で構築する。
 * tool 契約外の payload は空 dataset に落とさず fail-fast して誤った authoring を防ぐ。
 */
export function buildSaAggregatedDatasets(
  rawOutput: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  const output = expectVisualizationObjectOutput<
    Partial<GetSearchAnalyticsAggregatedDataOutput>
  >({
    toolName: GET_SEARCH_ANALYTICS_AGGREGATED_DATA_NAME,
    output: rawOutput,
  });

  const datasets: VizDataset[] = [];
  const context = isRecord(output.context) ? output.context : null;
  const requestedMetricFamilies =
    resolveRequestedMetricFamiliesWithLegacyFallback({
      expandedMetrics: context?.metrics,
      requestedMetrics: context?.requestedMetrics,
      metricFamilyByRequestKey: SA_REQUESTED_METRIC_FAMILIES,
      defaultMetricKeys: Object.keys(SA_REQUESTED_METRIC_FAMILIES),
      dependencyRules: SA_LEGACY_DEPENDENCY_RULES,
    });
  const groupBy = context ? (context.groupBy ?? null) : null;
  const items = extractArray(output, "items");
  const comparingItems = extractArray(output, "comparingItems");
  const isGroupedOutput =
    groupBy !== null ||
    hasSaGroupedItems(items) ||
    hasSaGroupedItems(comparingItems);
  const { currentDateRange, comparingDateRange } = resolveCompareDateRanges({
    output,
    includeContext: true,
  });
  const compareAlignmentMode = resolveCompareAlignmentMode({
    output,
    includeContext: true,
  });

  if (items.length > 0 && !isGroupedOutput) {
    const rows = attachDerivedRankDependencies(
      buildTimeseriesRowsFromMetrics({
        items,
        metrics: SA_TIMESERIES_METRICS,
      }),
      SA_TIMESERIES_DERIVED_RANK_DEPENDENCIES,
    );

    if (rows.length > 0) {
      datasets.push(
        buildDataset({
          id: "sa-agg-timeseries",
          profile: SA_TIMESERIES_PROFILE,
          fields: buildFields([
            { key: "date", role: "x", type: "date" },
            ...buildMetricFieldEntries(SA_TIMESERIES_METRICS),
          ]),
          rows,
          defaults: { x: "date", y: ["sumOfClicks"] },
          source,
        }),
      );
    }
  }

  datasets.push(
    ...buildSaGroupedDatasets({
      output,
      source,
      requestedMetricFamilies,
    }),
  );

  const groupSummaries = extractArray(output, "groupSummaries");
  if (groupSummaries.length > 0) {
    const rows = toDatasetRows(groupSummaries, (item) => {
      const group = isRecord(item.group) ? item.group : {};
      const groupLabel = toString(group.name ?? group.id);
      if (!groupLabel) return null;
      const row: Record<string, string | number | null> = {
        group: groupLabel,
      };
      for (const metric of SA_GROUP_METRICS) {
        const sourcePaths = metric.sourcePaths ?? [
          metric.sourceKey ?? metric.key,
        ];
        row[metric.key] = readFirstPathNumber({
          source: item,
          paths: sourcePaths,
        }).value;
      }
      return {
        ...row,
      };
    });

    if (rows.length > 0) {
      datasets.push(
        buildDataset({
          id: "sa-agg-groups",
          profile: SA_GROUPS_PROFILE,
          fields: buildFields([
            { key: "group", role: "dim", type: "string" },
            ...buildMetricFieldEntries(SA_GROUP_METRICS),
          ]),
          rows,
          defaults: { category: "group", value: "totalClicks" },
          source,
        }),
      );
    }
  }

  if (items.length > 0 && comparingItems.length > 0 && !isGroupedOutput) {
    const alignedRows = alignCompareTimeseries({
      currentItems: items,
      comparingItems,
      dateKey: "date",
      alignmentMode: compareAlignmentMode,
      currentDateRange,
      comparingDateRange,
    });

    const rows = attachDerivedRankDependencies(
      buildCompareTimeseriesRowsFromMetrics({
        alignedRows,
        metrics: SA_COMPARE_TIMESERIES_METRICS,
      }),
      SA_COMPARE_DERIVED_RANK_DEPENDENCIES,
    );

    if (rows.length > 0) {
      const fields = constrainMetricFieldsToFamilies({
        fields: buildFields([
          { key: "date", role: "x", type: "date" },
          { key: "comparingDate", role: "dim", type: "date" },
          ...buildCompareMetricFieldEntries(SA_COMPARE_TIMESERIES_METRICS),
        ]),
        metricFamilies: requestedMetricFamilies,
      });
      const supportsScatterPlot = hasCoRealizedMetricFamilyPair({
        rows,
        fields,
      });
      datasets.push(
        buildDataset({
          id: "sa-agg-compare",
          profile: resolveMetricChartProfile({
            profile: SA_COMPARE_PROFILE,
            rows,
            fields,
            supportsScatterPlot,
          }),
          fields,
          rows,
          defaults: {
            x: "date",
            category: "date",
            y: resolveCompareDefaultMetricKeys({
              fields,
              rows,
              preferredMetricFamily: "sumOfClicks",
            }),
          },
          source,
          notes: "Comparison dataset aligned by relative period index.",
        }),
      );
    }
  }

  return datasets;
}
